import { useQuery, gql } from '@apollo/client';

import * as immutable from 'object-path-immutable';
import _ from 'lodash';
import { defineMessages } from 'react-intl';

import toast from 'helpers/toast';

const REPORTS_PER_PAGE = 50;

const GET_REPORTS_FOR_MODAL = gql`
  query GetReportsForModal(
    $reportsPerPage: Int!
    $afterCursor: String
    $query: String
  ) {
    viewer {
      id
      reports(first: $reportsPerPage, after: $afterCursor, query: $query)
        @connection(key: "reportQuery") {
        edges {
          node {
            id
            uuid
            title
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

const getReportEdges = (data) => _.get(data, 'viewer.reports.edges', []);

const messages = defineMessages({
  fetchError: {
    id: 'Reports.getReports.fetchError',
    defaultMessage: 'There was a problem fetching the reports.',
    description: 'Error fetching reports message',
  },
});

const useGetReportsForModal = () => {
  const mergeReportEdges = (oldData, { fetchMoreResult: newData }) => {
    if (!newData) {
      return oldData;
    }

    const oldReportEdges = getReportEdges(oldData);
    const newReportEdges = getReportEdges(newData);

    const combinedReportEdges = [...oldReportEdges, ...newReportEdges];

    return immutable.set(newData, 'viewer.reports.edges', combinedReportEdges);
  };

  const { data, loading, fetchMore } = useQuery(GET_REPORTS_FOR_MODAL, {
    variables: { reportsPerPage: REPORTS_PER_PAGE },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
    onError: () => {
      toast.error(messages.fetchError);
    },
  });

  const reports = data?.viewer?.reports?.edges ?? [];
  const pageInfo = data?.viewer?.reports?.pageInfo ?? {};

  const fetchMoreReports = () => {
    if (pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          reportsPerPage: REPORTS_PER_PAGE,
          afterCursor: pageInfo.endCursor,
        },
        updateQuery: mergeReportEdges,
      });
    }
  };

  return { reports, reportsLoading: loading, fetchMoreReports };
};

export default useGetReportsForModal;
